import React, { useCallback, useEffect, useRef } from "react";
import {
  TextInput,
  TextInputHandle,
  TextInputType
} from "../../../../Forms/Inputs/TextInput/React";
import InputWrapperWithHeading from "../shared/inputWrapperWithHeading";

interface ValidatedTextInputFieldProps {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  errorMessage?: string;
  warningMessage?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
  inputType?: TextInputType;
  isValid: boolean;
  hasWarning?: boolean;
  hasBlurred: boolean;
  formValidated: boolean;
  forwardedRef?: React.RefObject<TextInputHandle>;
  onValueChange: (value: string) => void;
  onValidate?: (value: string) => boolean;
  onBlur?: () => void;
  onWarningCheck?: (value: string) => boolean;
}

/**
 * ValidatedTextInputField is a reusable form field that combines InputWrapperWithHeading
 * and TextInput with built-in validation logic.
 */
export const ValidatedTextInputField = React.memo(function ValidatedTextInputField({
  id,
  name,
  label,
  description,
  errorMessage,
  warningMessage,
  placeholder,
  value,
  required = false,
  inputType = TextInputType.Text,
  isValid,
  hasWarning = false,
  hasBlurred,
  formValidated,
  forwardedRef,
  onValueChange,
  onValidate,
  onBlur,
  onWarningCheck
}: ValidatedTextInputFieldProps) {
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handleValueChanged = useCallback(
    (newValue: string) => {
      onValueChange(newValue);
    },
    [onValueChange]
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const showError = !isValid && (formValidated || hasBlurred);
  const showWarning = hasWarning && !showError;

  return (
    <InputWrapperWithHeading
      id={id}
      label={label}
      description={description}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      required={required}
      invalid={showError}
      warning={showWarning}
    >
      <TextInput
        ref={forwardedRef}
        id={id}
        name={name}
        inputType={inputType}
        placeholder={placeholder}
        value={value}
        required={required}
        onValueChanged={handleValueChanged}
        onBlur={handleBlur}
        invalid={showError}
        warning={showWarning}
      />
    </InputWrapperWithHeading>
  );
});

export default ValidatedTextInputField;
